import type {UploadInfo, UploadEventProgress, UploadEventCompleted} from './lib/upload/types';
import type {FileSystemBase, FileInfo, DownloadProgress, DownloadResult} from './base';
import type {Dispatch} from 'redux';

const workers: {[id:string]: Worker} = {};

class FileSystem implements FileSystemBase {
  async pickFiles(): Promise<FileList> {
    return new Promise(resolve => {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = true;
      input.onchange = () => resolve(input.files);
      input.click();
    });
  }

  async pickMedia(_type?: 'photo' | 'video') {
    return [];
  }

  async receiveFiles(): Promise<FileInfo[]> {
    return [];
  }

  async clearReceivedFiles() {
    return;
  }

  async hashFile(
    file: File | string,
    progress: (bytes: number) => void,
    cancelId: string,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const worker = new Worker(new URL('./lib/hash/worker', import.meta.url), { type: 'module' });
      const hasher = workers[cancelId] = worker;
      hasher.onmessage = (e) => {
        switch (e.data.type) {
          case 'mf::hash::completed':
            resolve(e.data.payload);
            hasher.terminate();
            break;
          case 'mf::hash::progress':
            progress(e.data.payload);
            break;
          case 'mf::hash::failure':
            reject(e.data.payload);
            hasher.terminate();
            delete workers[cancelId];
            break;
        }
      };
      hasher.onerror = (e) =>
        reject(e.message ? e : new Error('Hash worker failed'));
      hasher.postMessage({file});
    });
  }

  cancelHashing(id: string) {
    const hasher = workers[id];
    if (hasher) {
      hasher.terminate();
      delete workers[id];
    }
  }

  async uploadFile(
    file: File,
    url: string,
    info: UploadInfo,
    progress: (e: UploadEventProgress) => void,
    progressInterval?: NodeJS.Timer,
  ): Promise<UploadEventCompleted> {
    return new Promise((resolve, reject) => {
      const id = info.id;
      const worker = new Worker(new URL('./lib/upload/worker', import.meta.url), { type: 'module' });
      const upload = workers[id] = worker;
      upload.onmessage = (event) => {
        switch (event.data.type) {
          case 'mf::upload::progress':
            progress(event.data.payload);
            break;
          case 'mf::upload::completed':
            upload.terminate();
            if (progressInterval) clearInterval(progressInterval as unknown as number);
            resolve(event.data.payload);
            break;
          case 'mf::upload::cancelled':
          case 'mf::upload::failure':
            upload.terminate();
            if (progressInterval) clearInterval(progressInterval as unknown as number);
            reject(event.data.payload);
            delete workers[id];
            break;
        }
      };
      upload.onerror = (e) =>
        reject(e.message ? e : new Error('Upload worker failed'));
      upload.postMessage({
        type: 'mf::upload::start',
        payload: {file, url, info},
      });
    });
  }

  async cancelUpload(id: string) {
    const upload = workers[id];
    if (upload) {
      upload.postMessage({
        type: 'mf::upload::cancel',
        payload: {id},
      });
    }
  }

  downloadDirectory = '';

  downloadFile(_fromUrl: string, _toFile: string, _progress: (e: DownloadProgress) => void): DownloadResult {
    return null;
  }

  cancelDownload(_id: number) {
    return null;
  }

  async viewFile(_fromUrl: string, _name: string, _dispatch: Dispatch, _actions: any) {
    return true; // No-op on web
  }

  async clearCookies() {
    try {
      document.cookie = '';
    } catch(e) {}
  }

  async getSecureItem(_key: string) {
    return undefined; // No-op on web
  }

  async setSecureItem(_key: string, _value: string) {
    return true; // No-op on web
  }
}

export default new FileSystem();
